import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Edit2, Loader2, Plus, Trash2 } from 'lucide-react';
import { commissionApi } from '@/services/api';
import type { CommissionRule } from '@/types';
import CommissionRuleFormModal from '@/components/commission/CommissionRuleFormModal';

function formatScopeSummary(rule: CommissionRule, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const scope = rule.apply_scope || (rule.item ? 'Specific Items' : rule.item_category ? 'Category' : 'All Sales');
  if (scope === 'All Sales') return t('commission.applyScope.allSalesShort');
  if (scope === 'Products') return t('commission.applyScope.productsShort');
  if (scope === 'Services') return t('commission.applyScope.servicesShort');
  if (scope === 'Category') {
    const names = rule.rule_categories?.map((c) => c.category_name || c.item_category).filter(Boolean);
    if (names?.length) return names.join(', ');
    return t('commission.applyScope.categoryShort', {
      name: rule.item_category_name || rule.item_category || '—',
    });
  }
  const count = rule.rule_items?.length ?? (rule.item ? 1 : 0);
  return t('commission.applyScope.itemsCount', { count });
}

function formatRuleSummary(rule: CommissionRule, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (rule.rule_type === 'Tiered Monthly') {
    const count = rule.tiers?.length ?? 0;
    if (!count) return t('commission.rules.noTiers');
    return t(count === 1 ? 'commission.rules.tiers' : 'commission.rules.tiers_plural', { count });
  }
  if (rule.rule_type === 'Fixed Per Unit') {
    return t('commission.rules.perUnit', { amount: rule.fixed_amount ?? 0 });
  }
  return `${rule.rate}%`;
}

export default function CommissionRulesList() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionRule | null>(null);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['commissionRules'],
    queryFn: async () => {
      const res = await commissionApi.listRules();
      return res.data.message as CommissionRule[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => commissionApi.deleteRule(name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['commissionRules'] }),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (rule: CommissionRule) => {
    setEditing(rule);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('commission.rules.title')}</h3>
          <p className="text-sm text-gray-500">{t('commission.rules.subtitle')}</p>
        </div>
        <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={openCreate}>
          <Plus className="w-4 h-4" />
          {t('commission.rules.addRule')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {rules.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">{t('commission.rules.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.rule')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.scope')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.type')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.basis')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.rateAmount')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.priority')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.rules.columns.status')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('commission.rules.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rules.map((rule) => (
                  <tr key={rule.name} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-gray-900">{rule.rule_name}</td>
                    <td className="px-4 py-3">{formatScopeSummary(rule, t)}</td>
                    <td className="px-4 py-3">{t(`commission.ruleTypes.${rule.rule_type}`, { defaultValue: rule.rule_type })}</td>
                    <td className="px-4 py-3">{t(`commission.basisTypes.${rule.basis}`, { defaultValue: rule.basis })}</td>
                    <td className="px-4 py-3">{formatRuleSummary(rule, t)}</td>
                    <td className="px-4 py-3">{rule.priority ?? 100}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          rule.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {rule.is_active ? t('commission.rules.statusActive') : t('commission.rules.statusInactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          onClick={() => openEdit(rule)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (rule.name && window.confirm(t('commission.rules.deleteConfirm', { name: rule.rule_name }))) {
                              deleteMutation.mutate(rule.name);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CommissionRuleFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        editing={editing}
      />
    </>
  );
}
